import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Plus, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";

import type { SearchableSelectOption } from "./searchable-select";

export interface MultiSearchableSelectProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: SearchableSelectOption[];
  /** Shown on the trigger when nothing is selected. */
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /**
   * Free-text entries that aren't in `options` (the Buy Box lets buyers add a
   * city the lookup table is missing). When set, an "Add …" row commits the
   * current query and the entries render as removable rows of their own.
   */
  allowCustom?: boolean;
  customValues?: string[];
  onCustomChange?: (values: string[]) => void;
}

/**
 * Multi-select dropdown with a type-to-filter search box. Selections toggle and
 * the panel stays open; selected items show a check. Closes on outside click or
 * Escape; keyboard up/down/enter to toggle the highlighted row.
 */
export function MultiSearchableSelect({
  values,
  onChange,
  options,
  placeholder = "All",
  className,
  disabled,
  allowCustom = false,
  customValues = [],
  onCustomChange,
}: MultiSearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedSet = useMemo(() => new Set(values), [values]);

  const triggerLabel = useMemo(() => {
    const total = values.length + customValues.length;
    if (total === 0) return placeholder;
    if (total === 1) {
      if (values.length === 1) return options.find((o) => o.value === values[0])?.label ?? placeholder;
      return customValues[0];
    }
    return `${total} selected`;
  }, [values, customValues, options, placeholder]);

  const rows = useMemo<SearchableSelectOption[]>(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;
  }, [options, query]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const toggle = (v: string) => {
    if (selectedSet.has(v)) onChange(values.filter((x) => x !== v));
    else onChange([...values, v]);
  };

  const trimmed = query.trim();
  // Offer "Add …" only for text that isn't already an option or an entry —
  // otherwise the buyer ends up with a free-text duplicate of a real lookup row.
  const canAddCustom =
    allowCustom &&
    trimmed.length >= 2 &&
    !options.some((o) => o.label.toLowerCase() === trimmed.toLowerCase()) &&
    !customValues.some((c) => c.toLowerCase() === trimmed.toLowerCase());

  const addCustom = () => {
    if (!canAddCustom) return;
    onCustomChange?.([...customValues, trimmed]);
    setQuery("");
  };

  const removeCustom = (v: string) => onCustomChange?.(customValues.filter((x) => x !== v));

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (rows[activeIdx]) toggle(rows[activeIdx].value);
      else addCustom();
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-lg border border-border/70 bg-background px-2.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/30 disabled:opacity-40",
          values.length + customValues.length === 0 && "text-muted-foreground",
          className,
        )}
      >
        <span className="truncate">{triggerLabel}</span>
        <span className="ml-1 flex shrink-0 items-center gap-1">
          {(values.length > 0 || customValues.length > 0) && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear selection"
              onClick={(e) => { e.stopPropagation(); onChange([]); onCustomChange?.([]); }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); onChange([]); onCustomChange?.([]); } }}
              className="rounded p-0.5 text-muted-foreground/70 hover:bg-accent hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-muted-foreground/70" />
        </span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-border/70 bg-popover shadow-lg">
          <div className="flex items-center gap-2 border-b border-border/60 px-2.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIdx(0);
              }}
              onKeyDown={onKeyDown}
              placeholder="Search…"
              className="h-9 w-full bg-transparent text-sm focus:outline-none"
            />
          </div>
          <ul className="max-h-60 overflow-y-auto py-1">
            {customValues.map((c) => (
              <li key={`custom-${c}`}>
                <button
                  type="button"
                  onClick={() => removeCustom(c)}
                  className="flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors hover:bg-accent"
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-primary bg-primary text-primary-foreground">
                    <Check className="h-3 w-3" />
                  </span>
                  <span className="truncate">{c}</span>
                  <span className="ml-auto shrink-0 text-[0.65rem] uppercase tracking-wide text-muted-foreground">
                    Custom
                  </span>
                </button>
              </li>
            ))}
            {rows.length === 0 && !canAddCustom && (
              <li className="px-3 py-2 text-sm text-muted-foreground">
                {allowCustom ? "No matches — keep typing to add it." : "No matches"}
              </li>
            )}
            {rows.map((o, idx) => {
              const checked = selectedSet.has(o.value);
              return (
                <li key={o.value}>
                  <button
                    type="button"
                    onClick={() => toggle(o.value)}
                    onMouseEnter={() => setActiveIdx(idx)}
                    className={cn(
                      "flex w-full items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors",
                      idx === activeIdx ? "bg-primary text-primary-foreground" : "hover:bg-accent",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors",
                        checked
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-muted-foreground/40 bg-background",
                        idx === activeIdx && !checked && "border-primary-foreground/60",
                      )}
                    >
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="truncate">{o.label}</span>
                  </button>
                </li>
              );
            })}
            {canAddCustom && (
              <li>
                <button
                  type="button"
                  onClick={addCustom}
                  className="flex w-full items-center gap-1.5 border-t border-border/50 px-3 py-2 text-left text-sm font-semibold text-primary transition-colors hover:bg-accent"
                >
                  <Plus className="h-3.5 w-3.5 shrink-0" />
                  Add "{trimmed}"
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
